import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DsButtonVariants, DsModalService } from '@levelaccess/design-system';
import { Subject } from 'rxjs';

import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';
import { SharedTextUtility } from '../../../../../shared/utils/text.utility';
import {
  PromptConfirmationModalOptions,
  PromptDialogListArgument,
  PromptDialogListItem,
  TranslationPromptArgument,
} from '../../../interfaces/modal-dialog.interface';
import { TranslateService } from '../../../translate/translate.service';
import { CustomValidators } from '../../../services/helpers/form-custom-validators';
import { InputFieldContext } from '../../common-input/common-input.component';
import { ErrorMessageService } from '../../../services/error-message.service';

enum FormFields {
  prompt = 'prompt',
}

@Component({
  selector: 'app-prompt-confirmation-modal',
  templateUrl: './prompt-confirmation-modal.component.html',
  styleUrls: ['./prompt-confirmation-modal.component.scss'],
  standalone: false,
})
export class PromptConfirmationModalComponent {
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;

  @Input() public closeAllModals: boolean = true;

  @Output() public confirm: EventEmitter<void> = new EventEmitter<void>();
  @Output() public cancelled: EventEmitter<void> = new EventEmitter<void>();

  private _options: PromptConfirmationModalOptions;

  @Input() public set options(value: PromptConfirmationModalOptions) {
    this._options = value;
    if (value) {
      this.updatePatternValidator();
    }
  }

  public get options(): PromptConfirmationModalOptions {
    return this._options;
  }

  public form: UntypedFormGroup;
  public formValidationRequest$: Subject<void> = new Subject();

  private closedProgrammatically: boolean = false;

  public constructor(
    protected modal: NgbActiveModal,
    private dsModalService: DsModalService,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private errorMessageService: ErrorMessageService,
    private element: ElementRef<Element>,
  ) {
    this.form = this.formBuilder.group({
      [FormFields.prompt]: new UntypedFormControl(null, [CustomValidators.required]),
    });
  }

  private updatePatternValidator(): void {
    const formControl: UntypedFormControl = this.form.get(FormFields.prompt) as UntypedFormControl;
    const pattern: string = this.translateService.instant(this.options.promptInputKey, this.options.promptInputParams);
    formControl.setValidators([CustomValidators.required, CustomValidators.pattern(pattern)]);
  }

  public get nameInputContext(): InputFieldContext {
    return {
      label: 'confirmation',
      field: FormFields.prompt,
      description: this.translateService.instant(this.options.promptConfirmKey, this.options.promptConfirmParams),
      overrideErrors: {
        pattern: this.translateService.instant('form_error_pattern_incorrect'),
      },
    };
  }

  private shouldTranslate<T extends PromptDialogListArgument>(value: T): value is T & TranslationPromptArgument {
    return SharedCommonUtility.notNullishOrEmpty((value as TranslationPromptArgument).translationKey);
  }

  public formatListItem(listItem: PromptDialogListItem): string {
    const translationArguments: string[] = listItem.arguments.map((item: PromptDialogListArgument) => {
      const value: string = this.shouldTranslate(item) ? this.translateService.instant(item.translationKey) : String(item.value);
      return item.highlight ? `<b>${SharedTextUtility.escapeHtmlTags(value)}</b>` : value;
    });
    return this.translateService.instant(listItem.messageKey, translationArguments);
  }

  public init(options: PromptConfirmationModalOptions): void {
    const defaultOptions: Partial<PromptConfirmationModalOptions> = {
      cancelButtonKey: 'label_cancel',
    };
    this.options = {
      ...defaultOptions,
      ...options,
    };
  }

  public onConfirm(): void {
    this.formValidationRequest$.next();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    this.closedProgrammatically = true;
    this.confirm.emit();
    this.dsModalService.closeCurrentModal();
  }

  public onCancel(): void {
    this.closedProgrammatically = true;
    this.cancelled.emit();
    this.dsModalService.closeCurrentModal();
  }

  public onClose(): void {
    if (!this.closedProgrammatically) {
      this.cancelled.emit();
    }
  }
}
